// index.html and public/sitemap.xml are checked-in mirrors, verified by seo.test.ts.
// Keep them in sync when changing SEO copy or public routes. The security entry is also emitted at build time for share crawlers.
export const SITE_ORIGIN = 'https://fj.need.run'
import type { AppLocale } from '../i18n/locales'
import zhTw from '../i18n/locales/zh-TW-seo.json'
import ja from '../i18n/locales/ja-seo.json'
import ko from '../i18n/locales/ko-seo.json'
export type SeoLocale = AppLocale
type Localized = Record<'zh-CN' | 'en', string>
const translatedPages: Record<string, Record<string, { title: string; description: string }>> = { 'zh-TW': zhTw, ja, ko }
const localeInfo = {
  'zh-CN': { name: '枫记', og: 'zh_CN', icon: '枫记枫叶图标', missing: '页面未找到 - 枫记', missingHint: '此页面不存在，请返回枫记首页继续使用。' },
  en: { name: 'Maple Log', og: 'en_US', icon: 'Maple Log maple leaf icon', missing: 'Page Not Found - Maple Log', missingHint: 'This page does not exist. Return to Maple Log to continue.' },
  'zh-TW': { name: '楓記', og: 'zh_TW', icon: '楓記楓葉圖示', missing: '找不到頁面 - 楓記', missingHint: '此頁面不存在，請返回楓記首頁繼續使用。' },
  ja: { name: 'Maple Log', og: 'ja_JP', icon: 'Maple Log のカエデの葉アイコン', missing: 'ページが見つかりません - Maple Log', missingHint: 'このページは存在しません。ホームに戻ってください。' },
  ko: { name: 'Maple Log', og: 'ko_KR', icon: 'Maple Log 단풍잎 아이콘', missing: '페이지를 찾을 수 없습니다 - Maple Log', missingHint: '존재하지 않는 페이지입니다. 홈으로 돌아가 주세요.' },
}
export interface SeoPage {
  path: string
  indexable: boolean
  title: Localized
  description: Localized
}

export const seoPages: readonly SeoPage[] = [
  { path: '/security', indexable: true,
    title: { 'zh-CN': '枫记安全与隐私报告 - 账号安全、技术原理与代码证据', en: 'Maple Log Safety & Privacy Report - Account Safety and Source Evidence' },
    description: { 'zh-CN': '枫记账号安全与封号疑问说明：收录经斗鱼主播联系官方检测组的聊天记录、主播来源及答复范围，并公开本机 OCR、权限边界、数据传输与源码证据。', en: 'Account safety and ban concerns: correspondence relayed through a Douyu streamer, its source and scope, plus local OCR, permissions, data transmission and source evidence.' } },
  { path: '/', indexable: true,
    title: { 'zh-CN': '枫记 - 冒险岛经验计算器与升级统计工具', en: 'Maple Log - MapleStory EXP and Leveling Tracker' },
    description: { 'zh-CN': '枫记是一款免费的冒险岛经验计算器与升级统计工具，支持每小时经验、金币收益、预计升级时间和练级地图效率对比。游戏画面在本机识别，历史记录保存在当前浏览器，当前支持国服怀旧服预设。', en: 'Free MapleStory EXP and leveling tracker with EXP per hour, Mesos gains, estimated level-up time, and map efficiency comparisons. Game frames are processed locally and history stays in your browser. Currently supports the China Classic preset.' } },
  { path: '/rank', indexable: true,
    title: { 'zh-CN': '冒险岛练级地图排行与经验效率对比 - 枫记', en: 'MapleStory Leveling Map Rankings - Maple Log' },
    description: { 'zh-CN': '查看小册子与枫记汇总的冒险岛练级地图排行，按等级段、职业和来源比较每小时经验，了解匿名样本门槛与统计规则，为练级选图提供参考。', en: 'Compare MapleStory map EXP efficiency by level range, job, and source. Learn how anonymous samples from Xiaoces and Maple Log qualify for the rankings.' } },
  { path: '/about', indexable: true,
    title: { 'zh-CN': '关于枫记 - 功能介绍、隐私与使用说明', en: 'About Maple Log - Features, Privacy and Usage' },
    description: { 'zh-CN': '了解枫记的本地游戏画面识别、经验与金币统计、浏览器历史记录，以及达标匿名经验汇总参与排行榜的方式，查看使用边界与作者反馈渠道。', en: 'Learn about local game-frame recognition, EXP and Mesos tracking, browser history, qualifying anonymous ranking summaries, usage limitations, and feedback channels.' } },
  { path: '/changelog', indexable: true,
    title: { 'zh-CN': '更新日志 - 枫记', en: 'Updates - Maple Log' },
    description: { 'zh-CN': '查看枫记的功能更新与改进记录，包括经验识别、金币统计、练级地图排行、小组件和性能优化。', en: 'Read Maple Log updates covering EXP recognition, Mesos statistics, map rankings, widgets, and performance improvements.' } },
  { path: '/history', indexable: false,
    title: { 'zh-CN': '历史记录 - 枫记', en: 'History - Maple Log' },
    description: { 'zh-CN': '查看保存在当前浏览器中的升级记录、经验增长、金币变化与效率总结。', en: 'Review leveling records, EXP gains, Mesos changes, and efficiency summaries saved in this browser.' } },
  { path: '/calibration', indexable: false,
    title: { 'zh-CN': '识别校准 - 枫记', en: 'Calibration - Maple Log' },
    description: { 'zh-CN': '调整游戏画面中经验、金币、HP/MP 与地图名称的识别位置。', en: 'Adjust recognition regions for EXP, Mesos, HP/MP, and map names in the game frame.' } },
  { path: '/settings', indexable: false,
    title: { 'zh-CN': '设置 - 枫记', en: 'Settings - Maple Log' },
    description: { 'zh-CN': '管理枫记的界面、识别性能、小组件与当前浏览器的本地数据。', en: 'Manage appearance, recognition performance, widgets, and local browser data.' } },
]

export function getSeo(path: string, locale: SeoLocale = 'zh-CN') {
  const pathname = path.split(/[?#]/u)[0]!
  const page = seoPages.find((item) => item.path === pathname)
  return {
    title: translatedPages[locale]?.[pathname]?.title ?? page?.title[locale === 'zh-CN' ? 'zh-CN' : 'en'] ?? localeInfo[locale].missing,
    description: translatedPages[locale]?.[pathname]?.description ?? page?.description[locale === 'zh-CN' ? 'zh-CN' : 'en'] ?? localeInfo[locale].missingHint,
    canonical: page ? new URL(page.path, SITE_ORIGIN).href : null,
    robots: page?.indexable ? 'index, follow, max-image-preview:large' : 'noindex, follow',
    image: `${SITE_ORIGIN}/icon-512.png`,
    locale,
    home: page?.path === '/',
  }
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/gu, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!)
}

export function renderSeoHead(path: string, locale: SeoLocale = 'zh-CN'): string {
  const seo = getSeo(path, locale)
  const meta = (key: string, content: string, property = false) => `<meta data-seo ${property ? 'property' : 'name'}="${key}" content="${escapeHtml(content)}">`
  const tags = [
    `<title data-seo>${escapeHtml(seo.title)}</title>`,
    meta('description', seo.description), meta('robots', seo.robots),
    meta('og:type', 'website', true), meta('og:site_name', localeInfo[locale].name, true),
    meta('og:locale', localeInfo[locale].og, true),
    meta('og:title', seo.title, true), meta('og:description', seo.description, true),
    meta('og:image', seo.image, true), meta('og:image:type', 'image/png', true),
    meta('og:image:width', '512', true), meta('og:image:height', '512', true),
    meta('og:image:alt', localeInfo[locale].icon, true),
    meta('twitter:card', 'summary'), meta('twitter:title', seo.title),
    meta('twitter:description', seo.description), meta('twitter:image', seo.image),
    meta('twitter:image:alt', localeInfo[locale].icon),
  ]
  if (seo.canonical) tags.push(`<link data-seo rel="canonical" href="${seo.canonical}">`, meta('og:url', seo.canonical, true))
  if (seo.home) {
    const data = { '@context': 'https://schema.org', '@graph': [
      { '@type': 'WebSite', '@id': `${SITE_ORIGIN}/#website`, url: `${SITE_ORIGIN}/`, name: '枫记', alternateName: 'Maple Log', inLanguage: 'zh-CN' },
      { '@type': 'SoftwareApplication', '@id': `${SITE_ORIGIN}/#application`, url: `${SITE_ORIGIN}/`, name: '枫记', alternateName: 'Maple Log', description: seo.description,
        image: seo.image, applicationCategory: 'UtilitiesApplication', operatingSystem: 'Windows, Web',
        browserRequirements: 'Requires a recent version of Google Chrome or Microsoft Edge', inLanguage: ['zh-CN', 'zh-TW', 'en', 'ja', 'ko'], isAccessibleForFree: true,
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'CNY' }, author: { '@type': 'Person', name: '小淳', url: 'https://github.com/qianjiachun/' } },
    ] }
    tags.push(`<script data-seo type="application/ld+json">${JSON.stringify(data).replace(/</gu, '\\u003c')}</script>`)
  }
  return tags.join('\n    ')
}

export function renderSitemap(): string {
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${seoPages.filter((page) => page.indexable).map((page) => `  <url><loc>${SITE_ORIGIN}${page.path}</loc></url>`).join('\n')}\n</urlset>\n`
}
